import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Chip, AddChip } from '@/components/Chip';
import { RecapCard } from '@/features/towns/RecapCard';
import { AddTown } from '@/features/towns/AddTown';
import { MeetingDetail } from '@/features/meeting/MeetingDetail';
import { useMediaQuery } from '@/lib/useMediaQuery';
import { fetchFollowedTowns, fetchMeetings } from '@/lib/api';
import { LATEST_TOWN_ID, type Meeting } from '@/lib/types';
import { canFollowMoreTowns, isProTier, TIERS } from '@/lib/tier';
import { useTier } from '@/session/SessionProvider';
import './MyTowns.css';

const PAGE_SIZE = 10;

/**
 * My Towns — the consumer home and the landing route at every tier.
 *
 * Layout is one system at two densities, which is the whole point of building this surface
 * and Signal Feed together:
 *
 *   resident, or below 1100px   single pane. Tapping a recap replaces the list with the
 *                               detail, mobile-app style, and "Back to list" returns.
 *   pro tier at 1100px and up   two panes. List (340–400px) and article side by side.
 *
 * An earlier design put towns in a persistent left column; that was rejected as a four-level
 * drill-down with too much cognitive load on mobile. Chips plus at most two panes is the
 * shipped IA — do not reintroduce the column.
 */
export function MyTowns() {
  const tier = useTier();
  const wideEnough = useMediaQuery('(min-width: 1100px)');
  const extraWide = useMediaQuery('(min-width: 1440px)');

  /**
   * Split view needs BOTH a pro tier and >=1100px — exactly as README § 1 states.
   *
   * `townsModel()` in the prototype appears to gate on width alone at 900px, but it is not
   * what renders: `feedPanes()` overrides its pane flags and computes
   * `split = pro && vw >= 1100`. 900px is a threshold used by other surfaces; it is not this
   * one. Read `feedPanes()`, not `townsModel()`, before changing any of this.
   *
   * A resident stays single-pane at every width, tapping through recaps the way they would on
   * a phone. That is deliberate: the tier continuum shows up in the layout itself, not only in
   * feature flags.
   */
  const split = isProTier(tier) && wideEnough;

  const [townId, setTownId] = useState<string>(LATEST_TOWN_ID);
  const [body, setBody] = useState('all');
  const [category, setCategory] = useState('all');
  const [meetingFilter, setMeetingFilter] = useState('all');
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [openMeeting, setOpenMeeting] = useState<Meeting | null>(null);
  const [addTownOpen, setAddTownOpen] = useState(false);

  // Tier is part of the key because entitlement changes the response — which towns are
  // followed, and how many are allowed. It also makes the dev tier switcher refetch.
  const townsQuery = useQuery({ queryKey: ['towns', tier], queryFn: fetchFollowedTowns });
  const towns = useMemo(() => townsQuery.data ?? [], [townsQuery.data]);

  const meetingsQuery = useQuery({
    queryKey: ['meetings', tier, townId, body, category, meetingFilter, limit],
    queryFn: () => fetchMeetings({ townId, body, category, meeting: meetingFilter, limit }),
    placeholderData: (previous) => previous, // keeps the list on screen while a filter changes
  });

  const page = meetingsQuery.data;
  const selectedTown = towns.find((t) => t.id === townId);
  const isLatest = townId === LATEST_TOWN_ID;
  const unreadTotal = towns.reduce((sum, t) => sum + t.newCount, 0);

  /** Changing scope resets paging and filters — a category from one town rarely fits another. */
  function pickTown(id: string) {
    setTownId(id);
    setBody('all');
    setCategory('all');
    // A meeting belongs to one town. Carrying the filter across would show an empty feed and
    // look like the new town has nothing in it.
    setMeetingFilter('all');
    setLimit(PAGE_SIZE);
    setOpenMeeting(null);
  }

  const bodyTabs = useMemo(() => {
    const counts = page?.bodyCounts ?? {};
    return (
      [
        { key: 'all', label: 'All' },
        { key: 'municipal', label: 'Council' },
        { key: 'boe', label: 'School board' },
      ] as const
    )
      .map((tab) => ({ ...tab, count: counts[tab.key] ?? 0 }))
      .filter((tab) => tab.key === 'all' || tab.count > 0);
  }, [page?.bodyCounts]);

  /** Categories are derived from what this scope actually discusses, not a fixed taxonomy. */
  const categories = useMemo(() => {
    const counts = page?.categoryCounts ?? {};
    const keys = Object.keys(counts).sort(
      (a, b) => (counts[b] ?? 0) - (counts[a] ?? 0) || a.localeCompare(b),
    );
    return [
      { key: 'all', label: 'All topics', count: page?.total ?? 0 },
      ...keys.map((key) => ({ key, label: key, count: counts[key] ?? 0 })),
    ];
  }, [page?.categoryCounts, page?.total]);

  /**
   * Pane visibility, from `feedPanes()`:
   *   list    shown unless a recap is open in a non-split layout
   *   detail  shown only when a recap is open — never as an empty pane waiting for one
   *   chips   hidden when a recap is open in the split layout, to give the article room
   *
   * So the list runs full-width until something is opened, then collapses to a rail. There is
   * no permanently empty right-hand pane.
   */
  const shown = page?.meetings ?? [];
  const openIndex = openMeeting ? shown.findIndex((m) => m.id === openMeeting.id) : -1;
  const open = Boolean(openMeeting);
  const showList = !open || split;
  const showDetail = open;
  const showChips = !open || !split;

  const canFollowMore = canFollowMoreTowns(tier, towns.length);
  const townCap = TIERS[tier].townCap;

  return (
    <div className="towns" data-split={split && open} data-extrawide={extraWide}>
      {showChips && (
      <div className="towns__chips u-scroller" role="tablist" aria-label="Followed towns">
        <Chip
          icon="zap"
          label="Latest"
          count={unreadTotal}
          selected={isLatest}
          onClick={() => pickTown(LATEST_TOWN_ID)}
        />
        {towns.map((town) => (
          <Chip
            key={town.id}
            icon="pin"
            label={town.name}
            count={town.newCount}
            selected={town.id === townId}
            onClick={() => pickTown(town.id)}
          />
        ))}
        <AddChip
          label={canFollowMore ? 'Follow meetings' : `${townCap} followed — at your limit`}
          onClick={() => setAddTownOpen(true)}
        />
      </div>
      )}

      <div className="towns__panes">
        {showList && (
          <section className="towns__list" aria-label="Recaps">
            <header className="towns__head">
              <h1 className="towns__title">{isLatest ? 'Latest' : (selectedTown?.name ?? '')}</h1>
              <p className="towns__subtitle">
                {isLatest
                  ? towns.length === 1
                    ? `Newest recaps from ${towns[0]?.name}.`
                    // No noun: what you follow may be a town, a county board or a state agency.
                    : `Newest recaps across everything you follow.`
                  : selectedTown
                    ? `${selectedTown.region} · ${selectedTown.meetingCount} meetings on file`
                    : ''}
              </p>
            </header>

            {bodyTabs.length > 1 && (
              <div className="towns__tabs" role="tablist" aria-label="Body type">
                {bodyTabs.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    role="tab"
                    aria-selected={body === tab.key}
                    className="towns__tab"
                    data-active={body === tab.key}
                    onClick={() => {
                      setBody(tab.key);
                      setCategory('all');
                      setLimit(PAGE_SIZE);
                    }}
                  >
                    {tab.label}
                    <span className="towns__tabCount">{tab.count}</span>
                  </button>
                ))}
              </div>
            )}

            {categories.length > 2 && (
              <div className="towns__cats u-scroller" aria-label="Topics">
                {categories.map((cat) => (
                  <Chip
                    key={cat.key}
                    label={
                      <>
                        {cat.label} <span className="towns__catCount">{cat.count}</span>
                      </>
                    }
                    selected={category === cat.key}
                    onClick={() => {
                      setCategory(cat.key);
                      setLimit(PAGE_SIZE);
                    }}
                  />
                ))}
              </div>
            )}

            {/* MEETINGS. Below the topics, because a topic narrows what kind of thing you are
                looking for and a meeting narrows which session it came from — and because the
                list of meetings is long enough to need its own scroller rather than sitting in
                the same row as the topics. */}
            {(page?.meetingOptions.length ?? 0) > 1 && (
              <div className="towns__meetings">
                <p className="u-meta towns__meetingsHead">Meetings</p>
                <div className="towns__meetingList u-scroller">
                  <Chip
                    label="All meetings"
                    selected={meetingFilter === 'all'}
                    onClick={() => {
                      setMeetingFilter('all');
                      setLimit(PAGE_SIZE);
                    }}
                  />
                  {page?.meetingOptions.map((option) => (
                    <Chip
                      key={option.id}
                      label={
                        <>
                          {option.date} · {option.title}{' '}
                          <span className="towns__catCount">{option.count}</span>
                          {option.isUpcoming && <span className="towns__upcoming">Upcoming</span>}
                        </>
                      }
                      selected={meetingFilter === option.id}
                      onClick={() => {
                        setMeetingFilter(option.id);
                        setLimit(PAGE_SIZE);
                      }}
                    />
                  ))}
                </div>
              </div>
            )}

            {meetingsQuery.isLoading && <RecapSkeleton />}

            {page && page.meetings.length === 0 && (
              <p className="towns__empty">
                Nothing matches this filter yet. Meetings are added as they’re processed.
              </p>
            )}

            {/* Meeting, then its summaries; next meeting, then its summaries. The API returns
                them already ordered that way, so grouping is a heading emitted when the meeting
                changes — not a re-sort, which would fight the server's ordering. */}
            {page?.meetings.map((meeting, i) => {
              const previous = i > 0 ? page.meetings[i - 1] : undefined;
              const startsMeeting = previous?.meetingId !== meeting.meetingId;
              return (
                <div key={meeting.id}>
                  {startsMeeting && (
                    <div className="towns__meetingHead">
                      <h2 className="towns__meetingTitle">{meeting.meetingTitle}</h2>
                      <p className="towns__meetingMeta">
                        {isLatest && <>{meeting.townName} · </>}
                        {meeting.date}
                        {meeting.isUpcoming && (
                          <span className="towns__upcoming">Upcoming — agenda only</span>
                        )}
                      </p>
                    </div>
                  )}
                  <RecapCard
                    meeting={meeting}
                    showTown={isLatest}
                    selected={openMeeting?.id === meeting.id}
                    onOpen={setOpenMeeting}
                  />
                </div>
              );
            })}

            {page && page.total > page.meetings.length && (
              <button
                type="button"
                className="towns__more"
                onClick={() => setLimit((n) => n + PAGE_SIZE)}
              >
                Show {page.total - page.meetings.length} more
              </button>
            )}
          </section>
        )}

        {showDetail && openMeeting && (
          <section className="towns__detail" aria-label="Recap detail">
            <MeetingDetail
              meetingId={openMeeting.id}
              onClose={() => setOpenMeeting(null)}
              closeLabel={split ? 'Close' : 'Back to list'}
              position={openIndex >= 0 ? `${openIndex + 1} of ${shown.length}` : undefined}
              onPrev={openIndex > 0 ? () => setOpenMeeting(shown[openIndex - 1]!) : undefined}
              onNext={
                openIndex >= 0 && openIndex < shown.length - 1
                  ? () => setOpenMeeting(shown[openIndex + 1]!)
                  : undefined
              }
            />
          </section>
        )}
      </div>

      {addTownOpen && (
        <AddTown
          followedNames={towns.map((t) => t.name)}
          onClose={() => setAddTownOpen(false)}
        />
      )}
    </div>
  );
}

function RecapSkeleton() {
  // Shaped like the real card so the layout does not jump when data lands — the static-app
  // trade-off for not server-rendering, and the reason first paint still feels solid.
  return (
    <div className="towns__skeleton" aria-hidden="true">
      {[0, 1, 2].map((i) => (
        <div key={i} className="towns__skelCard">
          <span className="towns__skelBar" style={{ width: '30%' }} />
          <span className="towns__skelBar" style={{ width: '80%', height: 16 }} />
          <span className="towns__skelBar" style={{ width: '100%' }} />
          <span className="towns__skelBar" style={{ width: '65%' }} />
        </div>
      ))}
    </div>
  );
}
